import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { createNote } from 'modules/notes/redux/noteActions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { AddPartnerNoteModalContextType } from '../types/AddPartnerNoteModalContextType';
import { addPartnerNoteModalInitValue } from '../constants/addPartnerNoteModalInitValue';

const AddPartnerNoteModalContext =
  createContext<AddPartnerNoteModalContextType>(addPartnerNoteModalInitValue);

export const AddPartnernoteModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  partnerId: string;
}> = ({ children, partnerId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [text, setText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  // eslint-disable-next-line
  const handleChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
  };

  const handleSavePartnerNote = async () => {
    try {
      setIsLoading(true);

      const noteData = {
        date: new Date(),
        text,
        reference: 'PARTNER',
        referenceId: partnerId,
        society: actualSociety?._id,
        userId: user._id,
      };

      await store.dispatch(createNote(noteData));
      setIsLoading(false);

      successSwal(t('AnnotationSavedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorSavingAnnotation'));
    } finally {
      handleCloseModal();
    }
  };

  return (
    <AddPartnerNoteModalContext.Provider
      value={{
        data: {
          text,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeText,
          handleSavePartnerNote,
        },
      }}
    >
      {children}
    </AddPartnerNoteModalContext.Provider>
  );
};

export const useAddPartnerNoteModal = () => {
  const context = useContext(AddPartnerNoteModalContext);
  if (!context) {
    throw new Error(
      'useAddPartnerNoteModal must be used within a AddPartnerNoteModalProvider'
    );
  }
  return context;
};
